import React, { useRef, useState, useEffect } from "react";
import { format, isSameDay, isWithinInterval } from 'date-fns';
import { Calendar as CalendarIcon, CaretLeft, CaretRight, CaretDown, CaretUp, X, Check } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const PRESETS = [
    { label: "All Time", value: "all_time" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "last_7" },
    { label: "This Week", value: "this_week" },
    { label: "Last Week", value: "last_week" },
    { label: "This Month", value: "this_month" },
    { label: "Last Month", value: "last_month" },
    { label: "Last 3 Months", value: "last_3_months" },
    { label: "Last 6 Months", value: "last_6_months" },
    { label: "This Quarter", value: "this_quarter" },
    { label: "Last Quarter", value: "last_quarter" },
    { label: "This Year", value: "this_year" },
    { label: "Custom Range", value: "custom" },
];

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getPresetRange(value) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (value) {
        case "all_time": return { start: null, end: null };
        case "today": return { start: today, end: today };
        case "yesterday": { const y = new Date(today); y.setDate(y.getDate() - 1); return { start: y, end: y }; }
        case "last_7": { const s = new Date(today); s.setDate(s.getDate() - 6); return { start: s, end: today }; }
        case "this_week": { const s = new Date(today); s.setDate(s.getDate() - s.getDay()); return { start: s, end: today }; }
        case "last_week": { const e = new Date(today); e.setDate(e.getDate() - e.getDay() - 1); const s = new Date(e); s.setDate(s.getDate() - 6); return { start: s, end: e }; }
        case "this_month": return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: today };
        case "last_month": { const s = new Date(today.getFullYear(), today.getMonth() - 1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); return { start: s, end: e }; }
        case "last_3_months": { const s = new Date(today); s.setMonth(s.getMonth() - 3); return { start: s, end: today }; }
        case "last_6_months": { const s = new Date(today); s.setMonth(s.getMonth() - 6); return { start: s, end: today }; }
        case "this_quarter": { const q = Math.floor(today.getMonth() / 3); const s = new Date(today.getFullYear(), q * 3, 1); return { start: s, end: today }; }
        case "last_quarter": { const q = Math.floor(today.getMonth() / 3); const s = new Date(today.getFullYear(), (q - 1) * 3, 1); const e = new Date(today.getFullYear(), q * 3, 0); return { start: s, end: e }; }
        case "this_year": return { start: new Date(today.getFullYear(), 0, 1), end: today };
        default: return { start: null, end: null };
    }
}

function parseSafeDate(d) {
    if (!d) return null;
    if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
    if (d.toDate && typeof d.toDate === 'function') return d.toDate();
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day);
    }
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? null : parsed;
}

function fmt(date) {
    if (!date) return "";
    try { return format(date, "MMM d, yyyy"); } catch (e) { return ""; }
}

function sameDay(a, b) {
    if (!a || !b) return false;
    return isSameDay(a, b);
}

function inRange(date, start, end) {
    if (!start || !end) return false;
    return isWithinInterval(date, {
        start: start < end ? start : end,
        end: start < end ? end : start
    });
}

function Calendar({ year, month, onNav, startDate, endDate, hovered, onHover, onSelect, selecting }) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

    const rangeEnd = selecting && hovered ? hovered : endDate;
    const today = new Date();

    return (
        <div style={{ width: 230 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onNav(-1); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", borderRadius: 6, color: "var(--text-tertiary)", display: 'flex', alignItems: 'center' }}>
                    <CaretLeft size={16} weight="bold" />
                </button>
                <span style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)", letterSpacing: -0.1, textTransform: 'uppercase' }}>{MONTHS[month]} {year}</span>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onNav(1); }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px", borderRadius: 6, color: "var(--text-tertiary)", display: 'flex', alignItems: 'center' }}>
                    <CaretRight size={16} weight="bold" />
                </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", textAlign: "center", marginBottom: 8 }}>
                {DAYS.map(d => <div key={d} style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: 'uppercase' }}>{d}</div>)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
                {cells.map((date, i) => {
                    if (!date) return <div key={`e${i}`} style={{ height: 30 }} />;
                    const isStart = sameDay(date, startDate);
                    const isEnd = sameDay(date, rangeEnd);
                    const isInRange = startDate && rangeEnd && inRange(date, startDate, rangeEnd);
                    const isToday = sameDay(date, today);
                    const isSelected = isStart || isEnd;

                    return (
                        <div
                            key={i}
                            onMouseEnter={() => onHover && onHover(date)}
                            onMouseLeave={() => onHover && onHover(null)}
                            onClick={(e) => { e.stopPropagation(); onSelect(date); }}
                            style={{
                                textAlign: "center",
                                height: 30,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: isSelected ? 800 : 600,
                                color: isSelected ? "#fff" : isInRange ? "var(--accent)" : isToday ? "var(--accent)" : "var(--text-primary)",
                                background: isSelected ? "#0f172a" : isInRange ? "var(--accent-subtle)" : "transparent",
                                borderRadius: isStart ? "6px 0 0 6px" : isEnd ? "0 6px 6px 0" : isInRange ? 0 : 6,
                                transition: "background 0.1s ease-out",
                                position: "relative",
                            }}
                        >
                            {date.getDate()}
                            {isToday && !isSelected && (
                                <div style={{ position: "absolute", bottom: 3, left: "50%", transform: "translateX(-50%)", width: 3, height: 3, borderRadius: "50%", background: "var(--accent)" }} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AnalogClock({ hour, minute, onHourChange, onMinuteChange, selecting: clockMode, onModeChange }) {
    const isHourMode = clockMode === 'hour';
    const numbers = isHourMode
        ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
        : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

    const activeValue = isHourMode ? hour : minute;

    const getAngle = (val) => {
        if (isHourMode) return ((val % 12) / 12) * 360 - 90;
        return (val / 60) * 360 - 90;
    };

    const handleClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const x = e.clientX - rect.left - cx;
        const y = e.clientY - rect.top - cy;
        let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
        if (angle < 0) angle += 360;

        if (isHourMode) {
            const h = Math.round(angle / 30) % 12 || 12;
            onHourChange(h);
            setTimeout(() => onModeChange('minute'), 200);
        } else {
            const m = Math.round(angle / 6) % 60;
            const snapped = Math.round(m / 5) * 5;
            onMinuteChange(snapped >= 60 ? 0 : snapped);
        }
    };

    const handAngle = getAngle(activeValue);

    return (
        <div
            onClick={handleClick}
            style={{
                width: '160px', height: '160px', borderRadius: '50%',
                background: 'var(--bg-subtle)', border: '2px solid var(--border)',
                position: 'relative', cursor: 'pointer', margin: '0 auto',
                transition: 'all 0.2s ease'
            }}
        >
            <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)', zIndex: 3
            }} />

            <motion.div
                animate={{ rotate: handAngle + 90 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                style={{
                    position: 'absolute', top: '50%', left: '50%',
                    width: '2px', height: '52px', background: 'var(--accent)',
                    transformOrigin: '50% 0%', borderRadius: '2px', zIndex: 2,
                    transform: `rotate(${handAngle + 90}deg)`
                }}
            />

            {numbers.map((num, i) => {
                const angle = (i / 12) * 360 - 90;
                const rad = (angle * Math.PI) / 180;
                const radius = 58;
                const x = 80 + Math.cos(rad) * radius;
                const y = 80 + Math.sin(rad) * radius;
                const isActive = num === activeValue || (!isHourMode && num === activeValue);

                return (
                    <motion.div
                        key={num}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.85 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isHourMode) {
                                onHourChange(num);
                                setTimeout(() => onModeChange('minute'), 200);
                            } else {
                                onMinuteChange(num);
                            }
                        }}
                        style={{
                            position: 'absolute',
                            left: `${x}px`, top: `${y}px`,
                            transform: 'translate(-50%, -50%)',
                            width: '28px', height: '28px', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '11px', fontWeight: isActive ? '800' : '600',
                            color: isActive ? '#fff' : 'var(--text-primary)',
                            background: isActive ? 'var(--accent)' : 'transparent',
                            transition: 'background 0.12s ease, color 0.12s ease',
                            cursor: 'pointer', zIndex: 4,
                            userSelect: 'none'
                        }}
                    >
                        {isHourMode ? num : String(num).padStart(2, '0')}
                    </motion.div>
                );
            })}
        </div>
    );
}

// Ensure backward compatibility by keeping DateTimePicker export alive, wrapping DateRangePicker
export function DateTimePicker(props) {
    return <DateRangePicker {...props} mode="single" includeTime={true} />;
}

export default function DateRangePicker({
    mode = 'range',
    includeTime = false,
    value,
    startDate,
    endDate,
    onChange,
    onRangeChange, // legacy support
    defaultPreset = 'today',
    placeholder = 'Select date...',
    className = '',
    compact = false,
    embedded = false
}) {
    const today = new Date();
    const [open, setOpen] = useState(false);
    const [activePreset, setActivePreset] = useState(defaultPreset);
    
    // Core Date states
    const safeStart = mode === 'single' ? parseSafeDate(value) : parseSafeDate(startDate);
    const safeEnd = mode === 'single' ? null : parseSafeDate(endDate);

    const [tempStart, setTempStart] = useState(safeStart);
    const [tempEnd, setTempEnd] = useState(safeEnd);
    const [selecting, setSelecting] = useState(false);
    const [hovered, setHovered] = useState(null);

    const initialMonthDate = safeStart || today;
    const [month, setMonth] = useState(initialMonthDate.getMonth());
    const [year, setYear] = useState(initialMonthDate.getFullYear());
    
    // Time & Zone states
    const [showTimeView, setShowTimeView] = useState(false);
    const [clockMode, setClockMode] = useState('hour');
    const [hour, setHour] = useState(safeStart ? (safeStart.getHours() % 12 || 12) : 9);
    const [minute, setMinute] = useState(safeStart ? safeStart.getMinutes() : 0);
    const [ampm, setAmpm] = useState(safeStart ? (safeStart.getHours() >= 12 ? 'PM' : 'AM') : 'AM');
    const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

    const ref = useRef();

    useEffect(() => {
        if (open) {
            const s = mode === 'single' ? parseSafeDate(value) : parseSafeDate(startDate);
            const e = mode === 'single' ? null : parseSafeDate(endDate);
            setTempStart(s); 
            setTempEnd(e);
            
            if (s) {
                setHour(s.getHours() % 12 || 12);
                setMinute(s.getMinutes());
                setAmpm(s.getHours() >= 12 ? 'PM' : 'AM');
            }

            const initDate = e || s || today;
            setMonth(initDate.getMonth()); 
            setYear(initDate.getFullYear());
            setShowTimeView(false);
        }
    }, [open, mode, value, startDate, endDate]);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        if (open) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const emitChange = (dateStart, dateEnd, h, m, ap) => {
        if (mode === 'single') {
            if (!dateStart) return;
            if (includeTime) {
                const hrs24 = ap === 'AM' ? (h === 12 ? 0 : h) : (h === 12 ? 12 : h + 12);
                const d = new Date(dateStart.getFullYear(), dateStart.getMonth(), dateStart.getDate(), hrs24, m);
                const offset = d.getTimezoneOffset();
                const local = new Date(d.getTime() - offset * 60000);
                onChange(local.toISOString().slice(0, 16));
            } else {
                onChange(dateStart.toISOString().slice(0, 10));
            }
        } else {
            // Range mode (time inclusion logic can be extended here if needed, keeping simple for now)
            if (onChange) onChange(dateStart, dateEnd);
            if (onRangeChange) onRangeChange(dateStart, dateEnd); // legacy
        }
    };

    function handlePreset(p) {
        setActivePreset(p.value);
        if (p.value !== "custom") {
            const range = getPresetRange(p.value);
            setTempStart(range.start); setTempEnd(range.end); setSelecting(false);
            emitChange(range.start, range.end, hour, minute, ampm);
            if (!includeTime) setOpen(false);
        } else {
            setTempStart(null); setTempEnd(null); setSelecting(false);
        }
    }

    function handleSelect(date) {
        if (mode === 'single') {
            setTempStart(date);
            emitChange(date, null, hour, minute, ampm);
            if (includeTime) {
                setClockMode('hour');
                setShowTimeView(true);
            } else {
                setOpen(false);
            }
        } else {
            if (!selecting || !tempStart) {
                setTempStart(date); setTempEnd(null); setSelecting(true); setActivePreset("custom");
            } else {
                let fs = tempStart, fe = date;
                if (date < tempStart) { fs = date; fe = tempStart; }
                setTempStart(fs); setTempEnd(fe); setSelecting(false);
                emitChange(fs, fe, hour, minute, ampm);
                if (!includeTime) setOpen(false);
            }
        }
    }

    function handleNav(dir) {
        let m = month + dir, y = year;
        if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
        setMonth(m); setYear(y);
    }

    const displayLabel = () => {
        const s = open ? tempStart : (mode === 'single' ? parseSafeDate(value) : parseSafeDate(startDate));
        const e = open ? tempEnd : (mode === 'single' ? null : parseSafeDate(endDate));

        if (mode === 'single') {
            if (!s) return placeholder;
            const dText = s.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            return includeTime ? `${dText} · ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${ampm}` : dText;
        }

        if (open && selecting && s) {
            if (hovered) {
                const rs = hovered < s ? hovered : s, re = hovered > s ? hovered : s;
                return sameDay(rs, re) ? fmt(rs) : `${fmt(rs)} – ${fmt(re)}`;
            }
            return fmt(s);
        }
        if (!s && !e) return "All Time";
        if (s && !e) return `${fmt(s)} – Select end`;
        if (sameDay(s, e)) return fmt(s);
        return `${fmt(s)} – ${fmt(e)}`;
    };

    return (
        <div ref={ref} className={className} style={{ position: "relative", width: compact ? 'auto' : "100%" }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    padding: compact ? '5px 10px' : '8px 12px',
                    borderRadius: '8px', cursor: 'pointer',
                    fontSize: compact ? '11px' : '13px',
                    color: (mode === 'single' ? tempStart : (tempStart || tempEnd)) ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    fontWeight: '600', whiteSpace: 'nowrap',
                    width: '100%', justifyContent: 'flex-start',
                    transition: 'border-color 0.15s ease'
                }}
            >
                <CalendarIcon size={compact ? 14 : 16} weight="bold" />
                <span style={{ flex: 1, textAlign: 'left' }}>{displayLabel()}</span>
                {!compact && <CaretDown size={14} weight="bold" style={{ marginLeft: 'auto', opacity: 0.5 }} />}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 5000,
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            borderRadius: '16px', boxShadow: '0 12px 40px -8px rgba(0,0,0,0.18)',
                            overflow: 'hidden',
                            display: 'flex', 
                            flexDirection: 'row',
                            width: showTimeView ? '260px' : (mode === 'range' ? '400px' : '260px'),
                            transition: 'width 0.2s ease'
                        }}
                    >
                        {!showTimeView ? (
                            <>
                                {/* Step 1: Calendar View */}
                                {mode === 'range' && (
                                    <div style={{ width: 140, borderRight: '1px solid var(--border)', padding: '12px 6px', overflowY: 'auto', maxHeight: 300, background: 'var(--bg-page)' }}>
                                        <div style={{ padding: '0 8px 8px', fontSize: '10px', fontWeight: 900, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Range</div>
                                        {PRESETS.map((p, idx) => (
                                            <motion.div
                                                key={p.value}
                                                initial={{ opacity: 0, x: -4 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: idx * 0.01 }}
                                                onClick={() => handlePreset(p)}
                                                style={{
                                                    padding: '6px 10px',
                                                    borderRadius: 8,
                                                    cursor: 'pointer',
                                                    fontSize: 12,
                                                    fontWeight: activePreset === p.value ? 800 : 500,
                                                    color: activePreset === p.value ? 'var(--accent)' : 'var(--text-secondary)',
                                                    background: activePreset === p.value ? 'var(--accent-subtle)' : 'transparent',
                                                    whiteSpace: 'nowrap',
                                                    marginBottom: '1px'
                                                }}
                                                whileHover={{ x: 2, background: activePreset === p.value ? 'var(--accent-subtle)' : 'var(--bg-hover)' }}
                                            >
                                                {p.label}
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                                
                                <div style={{ padding: '14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Calendar
                                        year={year} month={month}
                                        onNav={handleNav}
                                        startDate={tempStart} endDate={tempEnd}
                                        hovered={hovered} onHover={setHovered}
                                        onSelect={handleSelect} selecting={selecting}
                                    />
                                    
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                                        <button type="button" onClick={() => { setTempStart(null); setTempEnd(null); emitChange(null, null, hour, minute, ampm); if(!includeTime) setOpen(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)' }}>Clear</button>
                                        <button type="button" onClick={() => handleSelect(today)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600', color: 'var(--accent)' }}>Today</button>
                                        
                                        {includeTime && (
                                            <button
                                                type="button"
                                                onClick={() => { setClockMode('hour'); setShowTimeView(true); }}
                                                style={{
                                                    background: 'var(--bg-subtle)',
                                                    border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '700',
                                                    color: 'var(--text-secondary)',
                                                    padding: '3px 10px', borderRadius: '6px', transition: 'all 0.15s ease',
                                                    display: 'flex', alignItems: 'center', gap: '4px'
                                                }}
                                            >
                                                <CalendarIcon size={11} weight="bold" /> Time
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            /* Step 2: Time & Timezone View */
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                style={{ padding: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%' }}
                            >
                                <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '-4px' }}>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowTimeView(false)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--text-tertiary)', fontSize: '11px', fontWeight: '600' }}
                                    >
                                        <CaretLeft size={14} weight="bold" style={{ marginRight: '2px' }} /> Back
                                    </button>
                                    <span style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Time & Zone
                                    </span>
                                </div>

                                {/* Hour / Minute toggle */}
                                <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                    <button type="button" onClick={() => setClockMode('hour')} style={{
                                        flex: 1, padding: '5px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                        fontSize: '18px', fontWeight: '800', letterSpacing: '-0.02em',
                                        background: clockMode === 'hour' ? 'var(--accent-subtle)' : 'transparent',
                                        color: clockMode === 'hour' ? 'var(--accent)' : 'var(--text-tertiary)',
                                        transition: 'all 0.15s ease'
                                    }}>
                                        {String(hour).padStart(2, '0')}
                                    </button>
                                    <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-tertiary)', alignSelf: 'center' }}>:</span>
                                    <button type="button" onClick={() => setClockMode('minute')} style={{
                                        flex: 1, padding: '5px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                        fontSize: '18px', fontWeight: '800', letterSpacing: '-0.02em',
                                        background: clockMode === 'minute' ? 'var(--accent-subtle)' : 'transparent',
                                        color: clockMode === 'minute' ? 'var(--accent)' : 'var(--text-tertiary)',
                                        transition: 'all 0.15s ease'
                                    }}>
                                        {String(minute).padStart(2, '0')}
                                    </button>
                                </div>

                                {/* AM/PM */}
                                <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                    {['AM', 'PM'].map(ap => (
                                        <motion.button
                                            key={ap}
                                            type="button"
                                            whileTap={{ scale: 0.92 }}
                                            onClick={() => { setAmpm(ap); emitChange(tempStart, tempEnd, hour, minute, ap); }}
                                            style={{
                                                flex: 1, padding: '4px 0', borderRadius: '8px', border: 'none',
                                                fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                                                background: ampm === ap ? 'var(--accent)' : 'var(--bg-subtle)',
                                                color: ampm === ap ? '#fff' : 'var(--text-secondary)',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            {ap}
                                        </motion.button>
                                    ))}
                                </div>

                                {/* Analog Clock */}
                                <AnalogClock
                                    hour={hour}
                                    minute={minute}
                                    onHourChange={(h) => { setHour(h); emitChange(tempStart, tempEnd, h, minute, ampm); }}
                                    onMinuteChange={(m) => { setMinute(m); emitChange(tempStart, tempEnd, hour, m, ampm); }}
                                    selecting={clockMode}
                                    onModeChange={setClockMode}
                                />

                                {/* Timezone Selector */}
                                <div style={{ width: '100%', marginTop: '2px' }}>
                                    <select
                                        value={timezone}
                                        onChange={(e) => setTimezone(e.target.value)}
                                        style={{
                                            width: '100%', padding: '8px', borderRadius: '8px',
                                            border: '1px solid var(--border)', background: 'var(--bg-subtle)',
                                            fontSize: '11.5px', fontWeight: '600', color: 'var(--text-primary)',
                                            outline: 'none', cursor: 'pointer', appearance: 'none'
                                        }}
                                    >
                                        <option value="UTC">UTC (Universal)</option>
                                        <option value="America/New_York">EST (Eastern)</option>
                                        <option value="America/Chicago">CST (Central)</option>
                                        <option value="America/Denver">MST (Mountain)</option>
                                        <option value="America/Los_Angeles">PST (Pacific)</option>
                                        <option value="Europe/London">GMT (London)</option>
                                        <option value="Asia/Kolkata">IST (India)</option>
                                    </select>
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    style={{
                                        width: '100%', padding: '8px', borderRadius: '8px', border: 'none',
                                        background: 'var(--accent)', color: '#fff', fontSize: '12px', fontWeight: '700',
                                        cursor: 'pointer', marginTop: '4px'
                                    }}
                                >
                                    Done
                                </button>
                            </motion.div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
